import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Graph, ArrowsClockwise, Play, WarningCircle, TrashSimple, Robot, ClockCounterClockwise,
  Sparkle, Check, X, ArrowRight,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { formatJST } from '@/utils/datetime';
import { traceApi, clearTraceConfigCache } from '@/pages/trace/traceApi';

const RULE_LABEL = {
  rule_daily_report_project: '日報 → プロジェクト',
  rule_asset_from_request: '技術資産 → 部品依頼（作成元）',
  rule_dms_link_mirror: 'ドキュメント → 連携先（関連書類）',
  rule_asset_used_in_project: '技術資産 → プロジェクト（使用先）',
};

function Widget({ label, count, sample, nameKey, testid }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 shadow-sm" data-testid={testid}>
      <p className="text-[11px] font-medium text-zinc-400">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{count}</p>
      {(sample || []).length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {sample.map((s) => (
            <p key={s.id} className="text-[11px] text-zinc-500 truncate">・{s[nameKey] || s.name || s.request_no || s.report_date || s.id}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function AiSuggestionQueueCard() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, all: 0 });
  const [statusTab, setStatusTab] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(() => {
    traceApi.suggestionsQueue(statusTab)
      .then((r) => { setRows(r?.suggestions || []); setCounts(r?.counts || { pending: 0, all: 0 }); })
      .catch(() => { setRows([]); setCounts({ pending: 0, all: 0 }); });
  }, [statusTab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (s) => {
    setBusyId(s.id);
    try {
      await traceApi.approveSuggestion(s.id);
      toast.success('承認してリンクを作成しました');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '承認に失敗しました'); load(); }
    finally { setBusyId(null); }
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await traceApi.rejectSuggestion(rejectTarget.id, rejectReason.trim() || null);
      toast.success('AI候補を拒否しました');
      setRejectTarget(null); setRejectReason('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '拒否に失敗しました'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="bg-white rounded-xl border border-violet-200 p-5 shadow-sm space-y-3"
      data-testid="trace-ai-queue-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
            <Sparkle size={16} weight="fill" className="text-violet-600" /> AIリンク候補 承認キュー
          </p>
          <p className="text-xs text-zinc-500">
            各レコードから <span className="font-medium">「AIで候補を探す」</span> で作成された提案の一元承認画面。
            承認するとリンクが作成され、拒否すると理由と共に記録されます。
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-0.5 text-[11px]">
          {[
            { id: 'pending', label: `保留中 (${counts.pending})` },
            { id: 'approved', label: '承認済み' },
            { id: 'rejected', label: '拒否済み' },
            { id: 'all', label: `すべて (${counts.all})` },
          ].map((t) => (
            <button key={t.id} type="button" onClick={() => setStatusTab(t.id)}
              data-testid={`trace-ai-queue-tab-${t.id}`}
              className={`px-2 py-1 rounded-md font-medium transition-colors ${
                statusTab === t.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-400 py-4 text-center" data-testid="trace-ai-queue-empty">
          該当するAI候補はありません
        </p>
      ) : (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto" data-testid="trace-ai-queue-list">
          {rows.map((s) => {
            const conf = Math.round((s.confidence || 0) * 100);
            const tone = conf >= 75 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                         conf >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                                      'text-zinc-600 bg-zinc-50 border-zinc-200';
            return (
              <div key={s.id}
                className={`rounded-lg border px-2.5 py-2 space-y-1 ${
                  s.status === 'pending' ? 'border-violet-200 bg-violet-50/40' :
                  s.status === 'approved' ? 'border-emerald-200 bg-emerald-50/40' :
                  'border-zinc-200 bg-zinc-50 opacity-70'}`}
                data-testid={`trace-ai-queue-row-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-[12px]">
                  <Badge variant="outline" className="text-[9px] shrink-0">{s.source_type_label}</Badge>
                  <span className="font-medium text-zinc-800 truncate max-w-[160px]" title={s.source_label}>
                    {s.source_label}
                  </span>
                  <ArrowRight size={12} className="text-indigo-400 shrink-0" />
                  <Badge variant="outline" className="text-[9px] shrink-0">{s.target_type_label}</Badge>
                  <span className="font-medium text-zinc-800 truncate max-w-[160px]" title={s.target_label}>
                    {s.target_label}
                  </span>
                  <Badge variant="outline" className="text-[9px] shrink-0 text-indigo-600 border-indigo-200">
                    {s.relation_label_ja}
                  </Badge>
                  <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 border ${tone}`}>
                    {conf}%
                  </span>
                  <span className="text-[10px] text-zinc-400 ml-auto">{formatJST(s.created_at)}</span>
                </div>
                <p className="text-[11px] text-violet-800 leading-snug">
                  <span className="font-semibold">理由: </span>{s.reason_ja}
                </p>
                {s.status === 'pending' ? (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" onClick={() => approve(s)} disabled={busyId === s.id}
                      className="h-6 text-[11px] px-2 bg-teal-700 hover:bg-teal-800"
                      data-testid={`trace-ai-queue-approve-${s.id}`}>
                      <Check size={12} className="mr-1" /> 承認
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setRejectTarget(s); setRejectReason(''); }}
                      disabled={busyId === s.id}
                      className="h-6 text-[11px] px-2 text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`trace-ai-queue-reject-${s.id}`}>
                      <X size={12} className="mr-1" /> 拒否
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                    <Badge variant="outline" className={`text-[9px] ${
                      s.status === 'approved' ? 'text-emerald-600 border-emerald-200' :
                      'text-rose-500 border-rose-200'}`}>
                      {s.status === 'approved' ? '承認済み' : '拒否済み'}
                    </Badge>
                    <span>{s.reviewed_by_name} · {formatJST(s.reviewed_at)}</span>
                    {s.reject_reason && <span className="text-zinc-400">（{s.reject_reason}）</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {rejectTarget && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-2"
          data-testid="trace-ai-queue-reject-panel">
          <p className="text-xs text-rose-700 font-semibold">拒否理由（任意）</p>
          <input type="text" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="なぜ関連しないかを記録できます"
            className="w-full text-sm bg-white border border-rose-200 rounded-md px-2 py-1"
            data-testid="trace-ai-queue-reject-reason" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
              キャンセル
            </Button>
            <Button size="sm" onClick={doReject}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="trace-ai-queue-reject-confirm">
              拒否を確定
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TraceabilitySettings() {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [links, setLinks] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [bfResult, setBfResult] = useState(null);
  const [bfBusy, setBfBusy] = useState(false);

  const load = useCallback(() => {
    traceApi.config().then(setConfig).catch(() => {});
    traceApi.summary().then(setSummary).catch(() => setSummary(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    traceApi.linksAdmin(showDeleted).then(setLinks).catch(() => setLinks([]));
  }, [showDeleted, bfResult]);

  const saveSettings = async (patch) => {
    try {
      const fresh = await traceApi.putSettings(patch);
      setConfig((c) => ({ ...c, enabled: fresh.enabled, modules: fresh.modules }));
      clearTraceConfigCache();
      toast.success('設定を保存しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
  };

  const runBackfill = async (dryRun) => {
    setBfBusy(true);
    try {
      const res = await traceApi.backfill(dryRun);
      setBfResult(res);
      toast.success(dryRun ? 'ドライラン結果を取得しました' : 'バックフィルを実行しました');
      if (!dryRun) load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'バックフィルに失敗しました'); }
    finally { setBfBusy(false); }
  };

  const removeLink = async (lk) => {
    if (!window.confirm('このリンクを解除（ソフト削除）しますか？')) return;
    try {
      await traceApi.deleteLink(lk.id, '管理画面から解除');
      toast.success('リンクを解除しました');
      traceApi.linksAdmin(showDeleted).then(setLinks).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || '解除に失敗しました'); }
  };

  if (!config) return <div className="py-16 text-center text-sm text-zinc-400">読み込み中…</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="trace-settings-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Graph size={24} weight="duotone" className="text-indigo-600" /> 横断トレーサビリティ
        </h1>
        <p className="text-sm text-zinc-500 mt-1">モジュールをまたいだ「関連レコード」「履歴タイムライン」の一元管理</p>
      </div>

      {/* feature flags */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-800">機能を有効にする</p>
            <p className="text-xs text-zinc-500">OFF にすると全モジュールで関連レコード・タイムラインが非表示になります（データは保持されます）</p>
          </div>
          <Switch checked={config.enabled} onCheckedChange={(v) => saveSettings({ enabled: v })}
            data-testid="trace-global-toggle" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 border-t border-zinc-200">
          {(config.module_defs || []).map((m) => (
            <label key={m.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${config.enabled ? 'border-zinc-200' : 'border-zinc-100 opacity-50'}`}
              data-testid={`trace-module-${m.id}`}>
              <span className="text-xs font-medium text-zinc-700">{m.label_ja}</span>
              <Switch checked={!!config.modules?.[m.id]} disabled={!config.enabled}
                onCheckedChange={(v) => saveSettings({ modules: { [m.id]: v } })}
                data-testid={`trace-module-toggle-${m.id}`} />
            </label>
          ))}
        </div>
      </div>

      {/* backfill */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm space-y-3" data-testid="trace-backfill-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
              <ArrowsClockwise size={16} className="text-indigo-600" /> 既存データのバックフィル
            </p>
            <p className="text-xs text-zinc-500">既存レコード（日報のプロジェクト・資産の作成元など）から自動リンクを安全に生成します。まずドライランで内容を確認してください。</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={bfBusy} onClick={() => runBackfill(true)}
              data-testid="trace-backfill-dry">ドライラン</Button>
            <Button size="sm" disabled={bfBusy || !bfResult || !bfResult.dry_run} onClick={() => runBackfill(false)}
              className="bg-teal-700 hover:bg-teal-800" data-testid="trace-backfill-run">
              <Play size={13} className="mr-1" /> 実行
            </Button>
          </div>
        </div>
        {bfResult && (
          <div className="rounded-lg bg-zinc-50 p-3 space-y-1.5" data-testid="trace-backfill-result">
            <p className="text-[11px] font-semibold text-zinc-600">{bfResult.dry_run ? 'ドライラン結果（未反映）' : '実行結果'}</p>
            {Object.entries(bfResult.results || {}).map(([rule, r]) => (
              <div key={rule} className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-600">
                <span className="font-medium">{RULE_LABEL[rule] || rule}</span>
                <Badge variant="outline" className="text-[9px] text-indigo-600 border-indigo-200">
                  {bfResult.dry_run ? `作成予定 ${r.would_create ?? 0}` : `作成 ${r.created ?? 0}`}
                </Badge>
                <span className="text-zinc-400">既存スキップ {r.skipped_existing}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* management summary */}
      {summary && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
            <WarningCircle size={16} className="text-amber-500" /> トレーサビリティサマリー
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Widget label="関連リンクのないプロジェクト" count={summary.projects_without_links?.count || 0}
              sample={summary.projects_without_links?.sample} nameKey="name" testid="trace-w-projects" />
            <Widget label="作成元不明の技術資産" count={summary.assets_without_source?.count || 0}
              sample={summary.assets_without_source?.sample} nameKey="name" testid="trace-w-assets" />
            <Widget label="プロジェクト未設定の日報" count={summary.reports_without_project?.count || 0}
              sample={summary.reports_without_project?.sample} nameKey="report_date" testid="trace-w-reports" />
            <Widget label="未入荷の部品依頼" count={summary.requests_not_received?.count || 0}
              sample={summary.requests_not_received?.sample} nameKey="request_no" testid="trace-w-requests" />
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500" data-testid="trace-totals">
            <span>リンク合計 <b className="text-zinc-800 tabular-nums">{summary.totals?.links}</b></span>
            <span className="inline-flex items-center gap-1"><Robot size={13} /> 自動リンク <b className="text-zinc-800 tabular-nums">{summary.totals?.auto_links}</b></span>
            <span>解除済み <b className="text-zinc-800 tabular-nums">{summary.totals?.deleted_links}</b></span>
            <span className="inline-flex items-center gap-1"><ClockCounterClockwise size={13} /> イベント <b className="text-zinc-800 tabular-nums">{summary.totals?.events}</b></span>
          </div>
        </div>
      )}

      {/* AI suggestion queue */}
      <AiSuggestionQueueCard />

      {/* recent links */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-zinc-800">最近のリンク</p>
          <label className="flex items-center gap-2 text-xs text-zinc-500">
            解除済みも表示
            <Switch checked={showDeleted} onCheckedChange={setShowDeleted} data-testid="trace-show-deleted" />
          </label>
        </div>
        {links.length === 0 ? (
          <p className="text-xs text-zinc-400" data-testid="trace-admin-empty">リンクはまだありません</p>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="trace-admin-links">
            {links.map((lk) => (
              <div key={lk.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${lk.is_deleted ? 'border-zinc-100 bg-zinc-50 opacity-60' : 'border-zinc-200 bg-white'}`}
                data-testid={`trace-admin-link-${lk.id}`}>
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-[10px] text-zinc-400">{lk.source_type_label}</span> <span className="font-medium text-zinc-800">{lk.source_label}</span>
                  <span className="text-indigo-500 mx-1">→</span>
                  <span className="text-[10px] text-zinc-400">{lk.target_type_label}</span> <span className="font-medium text-zinc-800">{lk.target_label}</span>
                </span>
                <Badge variant="outline" className="text-[9px] shrink-0">{lk.relation_label_ja}</Badge>
                {lk.is_auto_created && <Badge variant="outline" className="text-[9px] shrink-0 text-violet-600 border-violet-200">自動</Badge>}
                {lk.is_deleted && <Badge variant="outline" className="text-[9px] shrink-0 text-rose-500 border-rose-200">解除済み</Badge>}
                <span className="text-[10px] text-zinc-400 shrink-0">{formatJST(lk.created_at)}</span>
                {!lk.is_deleted && (
                  <button type="button" onClick={() => removeLink(lk)} className="text-zinc-300 hover:text-rose-500 shrink-0"
                    data-testid={`trace-admin-unlink-${lk.id}`}>
                    <TrashSimple size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
